import { useEffect, useState } from 'react';

const fieldTypes = [
  'text',
  'email',
  'phone',
  'number',
  'textarea',
  'radio',
  'checkbox',
  'dropdown',
  'file',
  'date',
  'url',
];

const optionFieldTypes = ['dropdown', 'radio', 'checkbox'];

function slugify(value, fallback = 'field') {
  const slug = String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');

  return slug || fallback;
}

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function newCategory(order) {
  return {
    id: makeId('category'),
    title: 'New Category',
    slug: 'new-category',
    description: '',
    order,
    fields: [],
  };
}

function newField(order) {
  return {
    id: makeId('field'),
    type: 'text',
    label: 'New Field',
    key: `new-field-${order + 1}`,
    placeholder: '',
    helpText: '',
    required: false,
    options: [],
    allowedFileTypes: [],
    maxFileSizeMB: 10,
    order,
    active: true,
  };
}

function listToText(items = []) {
  return Array.isArray(items) ? items.join('\n') : String(items || '');
}

function textToList(value) {
  const source = Array.isArray(value) ? value : String(value || '').split(/\r?\n/);

  return source.map((item) => item.trim()).filter(Boolean);
}

function isOptionFieldType(type) {
  return optionFieldTypes.includes(type);
}

function getOptionText(field) {
  return typeof field.optionsText === 'string' ? field.optionsText : listToText(field.options);
}

function getOptionList(field) {
  return textToList(getOptionText(field));
}

function normalizeFormForSave(form) {
  return {
    ...form,
    categories: (form.categories || []).map((category) => ({
      ...category,
      fields: (category.fields || []).map((field) => {
        const persistedField = { ...field };
        const optionInput = persistedField.optionsText ?? persistedField.options;
        delete persistedField.optionsText;

        return {
          ...persistedField,
          options: isOptionFieldType(field.type) ? textToList(optionInput) : [],
        };
      }),
    })),
  };
}

function DynamicFormBuilder({
  defaultForm,
  eyebrow,
  heading,
  loadForm,
  pageClassName = '',
  saveForm,
  saveSuccessMessage,
  showIntroFields = false,
}) {
  const [form, setForm] = useState(defaultForm);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadForm()
      .then((data) => {
        setForm({ ...defaultForm, ...data.form });
        setLoading(false);
      })
      .catch(() => {
        setFeedback({ type: 'error', message: 'Unable to load form.' });
        setLoading(false);
      });
  }, [defaultForm, loadForm]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateCategory = (categoryIndex, field, value) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.map((category, index) => {
        if (index !== categoryIndex) {
          return category;
        }

        const next = { ...category, [field]: value };
        if (field === 'title') {
          next.slug = slugify(value, `category-${categoryIndex + 1}`);
        }
        return next;
      }),
    }));
  };

  const updateField = (categoryIndex, fieldIndex, field, value) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.map((category, index) => {
        if (index !== categoryIndex) {
          return category;
        }

        return {
          ...category,
          fields: category.fields.map((item, itemIndex) => {
            if (itemIndex !== fieldIndex) {
              return item;
            }

            const next = { ...item, [field]: value };
            if (field === 'label') {
              next.key = slugify(value, `field-${fieldIndex + 1}`);
            }
            return next;
          }),
        };
      }),
    }));
  };

  const addCategory = () => {
    setForm((current) => ({
      ...current,
      categories: [...current.categories, newCategory(current.categories.length)],
    }));
  };

  const removeCategory = (categoryIndex) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.filter((_, index) => index !== categoryIndex),
    }));
  };

  const moveCategory = (categoryIndex, direction) => {
    setForm((current) => {
      const categories = [...current.categories];
      const nextIndex = categoryIndex + direction;
      if (nextIndex < 0 || nextIndex >= categories.length) {
        return current;
      }
      [categories[categoryIndex], categories[nextIndex]] = [categories[nextIndex], categories[categoryIndex]];
      return {
        ...current,
        categories: categories.map((category, index) => ({ ...category, order: index })),
      };
    });
  };

  const addField = (categoryIndex) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.map((category, index) =>
        index === categoryIndex
          ? { ...category, fields: [...category.fields, newField(category.fields.length)] }
          : category
      ),
    }));
  };

  const removeField = (categoryIndex, fieldIndex) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.map((category, index) =>
        index === categoryIndex
          ? { ...category, fields: category.fields.filter((_, itemIndex) => itemIndex !== fieldIndex) }
          : category
      ),
    }));
  };

  const moveField = (categoryIndex, fieldIndex, direction) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.map((category, index) => {
        if (index !== categoryIndex) {
          return category;
        }

        const fields = [...category.fields];
        const nextIndex = fieldIndex + direction;
        if (nextIndex < 0 || nextIndex >= fields.length) {
          return category;
        }

        [fields[fieldIndex], fields[nextIndex]] = [fields[nextIndex], fields[fieldIndex]];
        return { ...category, fields: fields.map((field, order) => ({ ...field, order })) };
      }),
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback({ type: '', message: '' });

    try {
      const result = await saveForm(normalizeFormForSave(form));
      setForm({ ...defaultForm, ...result.form });
      setFeedback({ type: 'success', message: saveSuccessMessage });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to save form.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`admin-page admin-apply-cms-page ${pageClassName}`}>
      <div className="admin-page__header admin-page__header--split">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{heading}</h1>
        </div>
        <button className="btn btn--secondary" onClick={addCategory} type="button">
          Add Category
        </button>
      </div>

      {feedback.message ? (
        <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
          {feedback.message}
        </p>
      ) : null}

      {loading ? <p className="admin-empty">Loading form...</p> : null}

      <form className="admin-panel admin-apply-form-builder" onSubmit={handleSave}>
        <div className="admin-form-grid">
          <label>
            Form title
            <input onChange={(event) => updateForm('title', event.target.value)} required value={form.title} />
          </label>
          <label>
            Status
            <select onChange={(event) => updateForm('status', event.target.value)} value={form.status}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
        </div>

        {showIntroFields ? (
          <div className="admin-form-grid">
            <label>
              Intro title
              <input onChange={(event) => updateForm('introTitle', event.target.value)} value={form.introTitle || ''} />
            </label>
            <label>
              Intro description
              <textarea
                onChange={(event) => updateForm('introDescription', event.target.value)}
                rows="3"
                value={form.introDescription || ''}
              />
            </label>
          </div>
        ) : null}

        <div className="apply-cms-category-list">
          {form.categories.map((category, categoryIndex) => (
            <section className="apply-cms-category" key={category.id || category.slug}>
              <div className="apply-cms-category__top">
                <div>
                  <span>Category {categoryIndex + 1}</span>
                  <h2>{category.title}</h2>
                </div>
                <div className="apply-cms-actions">
                  <button onClick={() => moveCategory(categoryIndex, -1)} type="button">
                    Up
                  </button>
                  <button onClick={() => moveCategory(categoryIndex, 1)} type="button">
                    Down
                  </button>
                  <button onClick={() => removeCategory(categoryIndex)} type="button">
                    Delete
                  </button>
                </div>
              </div>

              <div className="admin-form-grid">
                <label>
                  Category title
                  <input
                    onChange={(event) => updateCategory(categoryIndex, 'title', event.target.value)}
                    required
                    value={category.title}
                  />
                </label>
                <label>
                  Category slug
                  <input
                    onChange={(event) => updateCategory(categoryIndex, 'slug', slugify(event.target.value))}
                    value={category.slug}
                  />
                </label>
              </div>
              <label>
                Description
                <textarea
                  onChange={(event) => updateCategory(categoryIndex, 'description', event.target.value)}
                  rows="2"
                  value={category.description}
                />
              </label>

              <div className="apply-cms-field-list">
                {category.fields.map((field, fieldIndex) => (
                  <article className="apply-cms-field" key={field.id || field.key}>
                    <div className="apply-cms-field__top">
                      <strong>{field.label}</strong>
                      <div className="apply-cms-actions">
                        <button onClick={() => moveField(categoryIndex, fieldIndex, -1)} type="button">
                          Up
                        </button>
                        <button onClick={() => moveField(categoryIndex, fieldIndex, 1)} type="button">
                          Down
                        </button>
                        <button onClick={() => removeField(categoryIndex, fieldIndex)} type="button">
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="admin-form-grid">
                      <label>
                        Label
                        <input
                          onChange={(event) => updateField(categoryIndex, fieldIndex, 'label', event.target.value)}
                          required
                          value={field.label}
                        />
                      </label>
                      <label>
                        Field key
                        <input
                          onChange={(event) =>
                            updateField(categoryIndex, fieldIndex, 'key', slugify(event.target.value))
                          }
                          value={field.key}
                        />
                      </label>
                      <label>
                        Type
                        <select
                          onChange={(event) => updateField(categoryIndex, fieldIndex, 'type', event.target.value)}
                          value={field.type}
                        >
                          {fieldTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Placeholder
                        <input
                          onChange={(event) =>
                            updateField(categoryIndex, fieldIndex, 'placeholder', event.target.value)
                          }
                          value={field.placeholder}
                        />
                      </label>
                    </div>

                    <label>
                      Help text
                      <input
                        onChange={(event) => updateField(categoryIndex, fieldIndex, 'helpText', event.target.value)}
                        value={field.helpText}
                      />
                    </label>

                    <div className="admin-form-grid">
                      {isOptionFieldType(field.type) ? (
                        <label>
                          Options
                          <span className="admin-label-help">One option per line</span>
                          <textarea
                            className="apply-cms-options-textarea"
                            onChange={(event) =>
                              updateField(categoryIndex, fieldIndex, 'optionsText', event.target.value)
                            }
                            placeholder="Option one&#10;Option two&#10;Option three"
                            rows="5"
                            value={getOptionText(field)}
                          />
                          {getOptionList(field).length === 0 ? (
                            <small className="apply-cms-warning">Add at least one option for this field.</small>
                          ) : null}
                        </label>
                      ) : null}
                      <label>
                        Allowed file types
                        <textarea
                          onChange={(event) =>
                            updateField(categoryIndex, fieldIndex, 'allowedFileTypes', textToList(event.target.value))
                          }
                          placeholder="pdf&#10;docx&#10;png"
                          rows="4"
                          value={listToText(field.allowedFileTypes)}
                        />
                      </label>
                    </div>

                    <div className="admin-form-grid">
                      <label>
                        Max file size MB
                        <input
                          min="1"
                          onChange={(event) =>
                            updateField(categoryIndex, fieldIndex, 'maxFileSizeMB', Number(event.target.value))
                          }
                          type="number"
                          value={field.maxFileSizeMB}
                        />
                      </label>
                      <div className="apply-cms-checks">
                        <label className="admin-inline-check">
                          <input
                            checked={field.required}
                            onChange={(event) =>
                              updateField(categoryIndex, fieldIndex, 'required', event.target.checked)
                            }
                            type="checkbox"
                          />
                          Required
                        </label>
                        <label className="admin-inline-check">
                          <input
                            checked={field.active}
                            onChange={(event) =>
                              updateField(categoryIndex, fieldIndex, 'active', event.target.checked)
                            }
                            type="checkbox"
                          />
                          Active
                        </label>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <button className="btn btn--secondary" onClick={() => addField(categoryIndex)} type="button">
                Add Field
              </button>
            </section>
          ))}
        </div>

        <div className="admin-job-editor__actions">
          <button className="btn btn--primary" disabled={saving} type="submit">
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          <button className="btn btn--secondary" onClick={addCategory} type="button">
            Add Category
          </button>
        </div>
      </form>
    </section>
  );
}

export default DynamicFormBuilder;
